import React, { useState } from "react";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Divider,
  IconButton,
  IconButtonProps,
  MenuItem,
  Menu as MenuMaterial,
  PopoverOrigin,
  Stack
} from "@mui/material";

interface Option {
  title: string;
  icon?: JSX.Element;
  onClick?: () => void;
  hidden?: boolean;
  onChange?: (e: any) => void;
  disabled?: boolean;
  hasLine?: boolean;
}

interface CustomOptionProps {
  closeMenu: () => void;
}

export interface MenuProps {
  iconButtonProps?: Omit<IconButtonProps, "onClick">;
  options?: Array<Option | React.FC<CustomOptionProps>>;
  icon?: JSX.Element;
  hidden?: boolean;
  keepTriggerEnabled?: boolean;
  horizontalTrigger?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

export const Menu: React.FC<MenuProps> = ({
  options = [],
  horizontalTrigger,
  icon = <MoreVertIcon sx={{ transform: horizontalTrigger ? "rotate(90deg)" : "none" }} />,
  hidden = false,
  keepTriggerEnabled = false,
  iconButtonProps,
  anchorOrigin,
  transformOrigin
}) => {
  const { sx, ...restIconButtonProps } = iconButtonProps || {};

  if (hidden) return null;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isTriggerActive, setIsTriggerActive] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const setTriggerNoActive = () => {
    setIsTriggerActive(false);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    if (keepTriggerEnabled) {
      setIsTriggerActive(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (keepTriggerEnabled) {
      setTriggerNoActive();
    }
  };

  return (
    <>
      <IconButton
        sx={{ ...sx, ...(isTriggerActive && { opacity: "1 !important" }) }}
        onClick={handleClick}
        {...restIconButtonProps}
      >
        {icon}
      </IconButton>
      <MenuMaterial
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        onClose={(event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          event.preventDefault();
          handleClose();
        }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
          sx: { borderRadius: "8px" },
          ...(anchorOrigin && { variant: "menu" })
        }}
      >
        {options
          .filter((option) => {
            if (typeof option === "object" && option.hidden) {
              return false;
            }
            return option;
          })
          .map((Item, index) => {
            if (typeof Item === "object") {
              const { onClick, icon, title, onChange, disabled, hasLine } = Item;
              return (
                <Stack key={index}>
                  <MenuItem
                    sx={{
                      display: "flex",
                      gap: "10px",
                      fontSize: "15px",
                      color: "#071F35",
                      ":hover": { bgcolor: "#ECF1F8" },
                      ...(disabled && { opacity: 0.5 })
                    }}
                    key={index}
                    onClick={(e) => {
                      if (onClick && !disabled) {
                        e.stopPropagation();
                        onClick?.();
                        handleClose();
                        if (keepTriggerEnabled) {
                          setTriggerNoActive();
                        }
                      }
                    }}
                    onChange={(e) => {
                      if (onChange) {
                        e.stopPropagation();
                        onChange(e);
                        handleClose();
                        if (keepTriggerEnabled) {
                          setTriggerNoActive();
                        }
                      }
                    }}
                  >
                    {icon && icon}
                    {title}
                  </MenuItem>
                  {hasLine && (
                    <Divider
                      sx={{
                        margin: "0px 12px 0px 12px",
                        height: "0.5px",
                        marginTop: "0px !important",
                        marginBottom: "0px !important",
                        borderColor: "#ced6d8"
                      }}
                    />
                  )}
                </Stack>
              );
            }
            return <Item closeMenu={handleClose} key={index} />;
          })}
      </MenuMaterial>
    </>
  );
};
